// Job Area State Management
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { AdminMaintenanceRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { IdName } from 'src/app/shared/models/id-name';
import { IJobArea } from '../models/job-area.model';
import { JobAreaApiService } from '../services/job-area-api.service';
import {
  CreateJobArea,
  DeleteJobArea,
  LoadAllJobAreas,
  LoadJobAreaById,
  LoadJobAreas,
  ClearCurrentJobArea,
  UpdateJobArea,
} from './job-area.actions';

export interface JobAreaStateModel {
  jobAreas: IJobArea[];
  allJobAreas: IJobArea[];
  jobAreasTotalPages: number;
  currentJobArea: IJobArea | null;
}

const JOB_AREA_STATE_TOKEN = new StateToken<JobAreaStateModel>('jobArea');

@State<JobAreaStateModel>({
  name: JOB_AREA_STATE_TOKEN,
  defaults: {
    jobAreas: [],
    allJobAreas: [],
    jobAreasTotalPages: 0,
    currentJobArea: null,
  },
})
@Injectable()
export class JobAreaState {
  constructor(private service: JobAreaApiService) {}

  @Action(LoadJobAreas)
  loadJobAreas({ getState, setState }: StateContext<JobAreaStateModel>, { filter }: LoadJobAreas) {
    return this.service.loadJobAreas(filter).pipe(
      tap((response: ApiResponse<IJobArea[]>) => {
        const state = getState();
        setState({
          ...state,
          jobAreas: response.data,
          jobAreasTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllJobAreas, { cancelUncompleted: true })
  loadAllJobAreas(ctx: StateContext<JobAreaStateModel>) {
    return this.service.loadAllJobAreas().pipe(
      tap((allJobAreas: IJobArea[]) => {
        ctx.setState(patch({ allJobAreas }));
      })
    );
  }

  @Action(LoadJobAreaById, { cancelUncompleted: true })
  loadJobAreaById(
    { getState, setState }: StateContext<JobAreaStateModel>,
    { id }: LoadJobAreaById
  ) {
    const { jobAreas } = getState();
    const exist = jobAreas.some((i: IJobArea) => i.id === id);
    return this.service.loadJobAreaById(id).pipe(
      tap((jobArea: IJobArea) => {
        setState(
          patch({
            jobAreas: exist
              ? updateItem<IJobArea>((toUpdate) => toUpdate?.id === id, jobArea)
              : insertItem<IJobArea>(jobArea),
            currentJobArea: jobArea,
          })
        );
      })
    );
  }

  @Action(CreateJobArea)
  @OnSuccess({ url: AdminMaintenanceRoutes.JobAreas, message: 'jobAreaAdded' })
  createJobArea({ setState }: StateContext<JobAreaStateModel>, { jobArea }: CreateJobArea) {
    return this.service.createJobArea(jobArea).pipe(
      tap((response: IJobArea) => {
        setState(
          patch({
            jobAreas: insertItem<IJobArea>(response),
          })
        );
      })
    );
  }

  @Action(UpdateJobArea)
  @OnSuccess({ url: AdminMaintenanceRoutes.JobAreas, message: 'jobAreaUpdated' })
  updateJobArea({ setState }: StateContext<JobAreaStateModel>, { jobArea }: UpdateJobArea) {
    return this.service.updateJobArea(jobArea).pipe(
      tap((response: IJobArea) => {
        setState(
          patch({
            jobAreas: updateItem<IJobArea>((toUpdate) => toUpdate?.id === jobArea.id, jobArea),
          })
        );
      })
    );
  }

  @Action(DeleteJobArea)
  @OnSuccess({
    url: AdminMaintenanceRoutes.JobAreas,
    message: 'jobAreaDeleted',
  })
  deleteJobArea(ctx: StateContext<JobAreaStateModel>, { id }: DeleteJobArea) {
    return this.service.deleteJobArea(id).pipe(
      tap(() => {
        ctx.setState(
          patch({
            jobAreas: removeItem<IJobArea>((jobArea) => jobArea?.id === id),
          })
        );
        ctx.dispatch(new ResetCurrentFilter());
      })
    );
  }

  @Action(ClearCurrentJobArea)
  clearCurrentJobArea(ctx: StateContext<JobAreaStateModel>) {
    ctx.setState(patch({ currentJobArea: null }));
  }

  @Selector()
  static getJobAreasTotalPages(state: JobAreaStateModel) {
    return state.jobAreasTotalPages;
  }

  @Selector()
  static jobAreas(state: JobAreaStateModel) {
    return state.jobAreas;
  }

  @Selector()
  static allJobAreas(state: JobAreaStateModel): IJobArea[] {
    return state.allJobAreas;
  }

  @Selector()
  static allJobAreasMapped(state: JobAreaStateModel): IdName[] {
    return state.allJobAreas.map((jobArea) => ({
      id: jobArea.id,
      name: `${jobArea.prefix} ${jobArea.name}`,
    }));
  }

  @Selector()
  static getJobAreaById(state: JobAreaStateModel) {
    return (id: string) => {
      return state.jobAreas.find((jobArea: IJobArea) => jobArea.id === id);
    };
  }

  @Selector()
  static getCurrentJobArea(state: JobAreaStateModel) {
    return state.currentJobArea;
  }
}
